if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (window.__dfyCartAddPending) return;

        window.__dfyCartAddPending = true;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await this.requestCartAdd(config, formData);
          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          try {
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          } catch (subscriberError) {
            console.warn('[Shopify product-form] cart subscriber failed after successful add', subscriberError);
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => this.renderCartContents(response).catch((renderError) => {
                console.error('[Shopify product-form] delayed cart drawer render failed', renderError);
                this.handleErrorMessage('Item added. Please open your cart to review it.');
              }),
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            try {
              await this.renderCartContents(response);
            } catch (renderError) {
              console.error('[Shopify product-form] item added but cart drawer render failed', renderError);
              this.handleErrorMessage('Item added. Please open your cart to review it.');
            }
          }
        } catch (error) {
          console.error('[Shopify product-form] add to cart failed', {
            message: error.message,
            status: error.httpStatus,
            response: error.response,
            variantId: formData.get('id'),
            quantity: formData.get('quantity'),
          });
          this.handleErrorMessage(error.message || 'Unable to add this item. Please try again.');
        } finally {
          window.__dfyCartAddPending = false;
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      async requestCartAdd(config, formData) {
        const unconfirmedMessage = "We couldn't confirm whether the item was added. Please check your cart before trying again.";
        let httpResponse;
        let responseText;
        try {
          // Never repeat an add POST after an ambiguous response: Shopify may
          // already have added the item before the response was interrupted.
          httpResponse = await fetch(`${routes.cart_add_url}`, config);
          responseText = await httpResponse.text();
        } catch (error) {
          const requestError = new Error(unconfirmedMessage);
          requestError.cause = error;
          requestError.cartOutcomeUnknown = true;
          throw requestError;
        }
        const isEdgeChallenge =
          httpResponse.headers.get('cf-mitigated') === 'challenge' ||
          /verifying your connection|cf-chl-|challenge-platform/i.test(responseText);
        let responseBody;

        try {
          responseBody = JSON.parse(responseText);
        } catch (parseError) {
          console.error('[Shopify product-form] /cart/add.js returned non-JSON', {
            status: httpResponse.status,
            statusText: httpResponse.statusText,
            response: responseText,
            variantId: formData.get('id'),
            quantity: formData.get('quantity'),
          });
          const requestError = new Error(unconfirmedMessage);
          requestError.httpStatus = httpResponse.status;
          requestError.response = responseText;
          requestError.isEdgeChallenge = isEdgeChallenge;
          requestError.cartOutcomeUnknown = true;
          throw requestError;
        }

        if (!responseBody || typeof responseBody !== 'object') {
          const requestError = new Error(unconfirmedMessage);
          requestError.cartOutcomeUnknown = true;
          throw requestError;
        }

        if (!httpResponse.ok || responseBody.status >= 400) {
          const status = Number(responseBody.status) || httpResponse.status;
          console.error('[Shopify product-form] /cart/add.js failed', {
            status,
            response: responseBody,
            variantId: formData.get('id'),
            quantity: formData.get('quantity'),
          });
          const requestError = new Error(status >= 500
            ? unconfirmedMessage
            : responseBody.description || responseBody.message || 'Add to cart failed');
          requestError.httpStatus = status;
          requestError.response = responseBody;
          requestError.cartOutcomeUnknown = status >= 500;
          throw requestError;
        }

        return responseBody;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      async renderCartContents(parsedState) {
        const requiredSections = this.cart.getSectionsToRender().map((section) => section.id);
        const hasCompleteSections =
          parsedState.sections && requiredSections.every((sectionId) => parsedState.sections[sectionId]);

        if (hasCompleteSections) {
          try {
            this.cart.renderContents(parsedState);
            return;
          } catch (renderError) {
            console.warn('[Shopify product-form] initial cart drawer render failed; refreshing sections', renderError);
          }
        }

        const sectionUrl = `${window.location.pathname}${window.location.search}`;
        const refreshUrl = `${routes.cart_url}?sections=${encodeURIComponent(requiredSections.join(','))}&sections_url=${encodeURIComponent(sectionUrl)}`;
        const refreshResponse = await fetch(refreshUrl, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const refreshText = await refreshResponse.text();
        let refreshedSections;

        try {
          refreshedSections = JSON.parse(refreshText);
        } catch (parseError) {
          console.error('[Shopify product-form] cart section refresh returned non-JSON', {
            status: refreshResponse.status,
            response: refreshText,
          });
          throw new Error('Item was added, but the cart drawer could not refresh. Please open your cart to review it.');
        }

        if (
          !refreshResponse.ok ||
          !refreshedSections ||
          !requiredSections.every((sectionId) => refreshedSections[sectionId])
        ) {
          console.error('[Shopify product-form] cart section refresh failed', {
            status: refreshResponse.status,
            response: refreshedSections,
            requiredSections,
          });
          throw new Error('Item was added, but the cart drawer could not refresh.');
        }

        this.cart.renderContents({ ...parsedState, sections: refreshedSections });
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
